import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Loader2, FileWarning } from "lucide-react";

export const extOf = (n) => ((n || "").includes(".") ? n.split(".").pop().toLowerCase() : "");
const KIND = { pdf: "pdf", docx: "docx", doc: "doc", xlsx: "sheet", xls: "sheet", png: "image", jpg: "image", jpeg: "image", webp: "image", gif: "image" };
const MIME = { pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", gif: "image/gif" };
export const previewKind = (name, ct) => KIND[extOf(name)] || ((ct || "").startsWith("image/") ? "image" : ct === "application/pdf" ? "pdf" : null);

function SheetView({ wb }) {
  const [idx, setIdx] = useState(0);
  const XLSX = wb.lib;
  const html = XLSX.utils.sheet_to_html(wb.book.Sheets[wb.book.SheetNames[idx]], { header: "", footer: "" });
  return (
    <div className="flex flex-col h-full">
      {wb.book.SheetNames.length > 1 && (
        <div className="flex gap-1 flex-wrap mb-2" data-testid="viewer-sheet-tabs">
          {wb.book.SheetNames.map((s, i) => (
            <button key={s} type="button" onClick={() => setIdx(i)} data-testid={`viewer-sheet-tab-${i}`}
              className={`px-3 py-1 rounded text-xs transition-colors ${i === idx ? "bg-[#007AFF] text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>{s}</button>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-auto bg-white text-zinc-900 rounded-md text-xs [&_table]:border-collapse [&_td]:border [&_td]:border-zinc-300 [&_td]:px-2 [&_td]:py-1 [&_td]:whitespace-nowrap [&_tr:first-child_td]:bg-zinc-100 [&_tr:first-child_td]:font-semibold"
        data-testid="viewer-sheet" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

function Body({ kind, data, docxRef }) {
  if (kind === "pdf") return <iframe src={data.url} title="Aperçu PDF" className="w-full h-full rounded-md bg-white" data-testid="viewer-pdf" />;
  if (kind === "image") return <div className="h-full flex items-center justify-center"><img src={data.url} alt="" className="max-h-full max-w-full object-contain" data-testid="viewer-image" /></div>;
  if (kind === "sheet") return <SheetView wb={data} />;
  if (kind === "docx") return <div ref={docxRef} className="h-full overflow-auto bg-zinc-200 rounded-md" data-testid="viewer-docx" />;
  return (
    <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-zinc-400" data-testid="viewer-unsupported">
      <FileWarning className="w-10 h-10" />
      <p className="text-sm">Aperçu indisponible pour ce format{kind === "doc" ? " (ancien Word .doc)" : ""}.</p>
      <p className="text-xs">Téléchargez le fichier pour l'ouvrir.</p>
    </div>
  );
}

// file = { name, url, downloadUrl, content_type }
export default function FileViewer({ file: incoming, onClose }) {
  const last = useRef(null);
  if (incoming) last.current = incoming;
  const file = last.current;
  const [state, setState] = useState({ loading: false, error: "", data: null, buf: null });
  const docxRef = useRef(null);
  const kind = file ? previewKind(file.name, file.content_type) : null;

  useEffect(() => {
    if (!file || !kind || kind === "doc") { setState({ loading: false, error: "", data: null, buf: null }); return undefined; }
    let url = null;
    let off = false;
    setState({ loading: true, error: "", data: null, buf: null });
    (async () => {
      try {
        const r = file.load ? null : await fetch(file.url);
        if (r && !r.ok) throw new Error();
        const buf = file.load ? await file.load() : await r.arrayBuffer();
        let data = null;
        if (kind === "sheet") {
          const lib = await import("xlsx");
          data = { lib, book: lib.read(buf, { type: "array" }) };
        } else if (kind !== "docx") {
          url = URL.createObjectURL(new Blob([buf], { type: MIME[extOf(file.name)] || file.content_type || "application/pdf" }));
          data = { url };
        }
        if (!off) setState({ loading: false, error: "", data, buf });
      } catch {
        if (!off) setState({ loading: false, error: "Impossible de charger le fichier", data: null, buf: null });
      }
    })();
    return () => { off = true; if (url) URL.revokeObjectURL(url); };
  }, [file, kind]);

  useEffect(() => {
    if (kind !== "docx" || !state.buf || !docxRef.current) { return; }
    import("docx-preview").then((m) => m.renderAsync(state.buf, docxRef.current, null, { inWrapper: true, ignoreLastRenderedPageBreak: true }))
      .catch(() => setState((s) => ({ ...s, error: "Document Word illisible" })));
  }, [kind, state.buf]);

  return (
    <Dialog open={!!incoming} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-5xl w-[95vw] h-[88vh] flex flex-col z-[100]" data-testid="file-viewer">
        <DialogHeader className="flex-row items-center gap-3 pr-8 space-y-0">
          <DialogTitle className="truncate text-base" data-testid="file-viewer-name">{file?.name}</DialogTitle>
          {file?.onDownload && (
            <button type="button" onClick={file.onDownload} data-testid="file-viewer-download" className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-xs rounded-md border border-white/10 px-2.5 py-1.5 text-zinc-300 hover:text-white hover:border-[#007AFF] transition-colors">
              <Download className="w-3.5 h-3.5" /> Télécharger
            </button>
          )}
          {file?.downloadUrl && (
            <a href={file.downloadUrl} data-testid="file-viewer-download" className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-xs rounded-md border border-white/10 px-2.5 py-1.5 text-zinc-300 hover:text-white hover:border-[#007AFF] transition-colors">
              <Download className="w-3.5 h-3.5" /> Télécharger
            </a>
          )}
        </DialogHeader>
        <div className="flex-1 min-h-0">
          {state.loading ? <div className="h-full flex items-center justify-center" data-testid="file-viewer-loading"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
            : state.error ? <p className="text-sm text-[#FF3B30] text-center pt-10" data-testid="file-viewer-error">{state.error}</p>
            : <Body kind={kind || "none"} data={state.data} docxRef={docxRef} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
